import { useCallback, useRef } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import dayjs from 'dayjs';

import { MDText } from 'i18n-react';

import { replaceHyphensWithSpace, upperCaseString } from 'Utilities/breadcrumbs';
import { assignSectionRoutes } from 'Utilities/routing';

import { WordpressRouteParams } from '../@types/Wordpress';
import { DATE_LIMITS, VALID_YEAR_RANGE, WORDPRESS_PATHS } from '../utilities/WordpressConstants';
import LocalTexts from '../utilities/WordpressText.json';

const LocalT = new MDText(LocalTexts);

export const useBreadcrumbNavigation = () => {
  const params = useParams<WordpressRouteParams>();
  const match = useRouteMatch();

  const {
    isBlogRoute,
    isGuideAuthorDetailsRoute,
    isGuideAuthorsRoute,
  } = assignSectionRoutes({ params: match.params, path: match.path });
  const blogTitle = useRef<string | null>(null);

  const formatDateComponent = useCallback(({ dateStr, maxValue, useDefaultPadding }: {
    dateStr?: string;
    maxValue: number;
    useDefaultPadding?: boolean;
  }): string | null => {
    const dateNum = +(dateStr || 0);
    const defaultValue = useDefaultPadding ? '01' : null;

    const isValidDate = !Number.isNaN(dateNum) && dateNum > 0 && dateNum <= maxValue;
    return isValidDate ? dateNum.toString().padStart(2, '0') : defaultValue;
  }, []);

  const isValidYear = useCallback((year: string): boolean => {
    const yearNum = Number.parseInt(year);
    return !Number.isNaN(yearNum)
      && yearNum >= VALID_YEAR_RANGE.MIN
      && yearNum <= VALID_YEAR_RANGE.MAX;
  }, []);

  const buildDateConfig = useCallback((year: string, month?: string, day?: string) => {
    const yearNum = Number.parseInt(year);
    const formattedMonth = formatDateComponent({ dateStr: month, maxValue: DATE_LIMITS.MONTH });
    const formattedDay = formatDateComponent({ dateStr: day, maxValue: DATE_LIMITS.DAY });

    // Handle edge case: `/blog/2018/00/12` -> month becomes '01'
    const finalMonth = !formattedMonth && formattedDay ? '01' : formattedMonth;

    const date = dayjs([yearNum, finalMonth || '01', formattedDay || '01'].join('-'));

    return {
      day: formattedDay,
      dayLabel: date.format('dddd Do'),
      month: finalMonth,
      monthLabel: date.format('MMMM'),
      year: yearNum.toString(),
      yearLabel: yearNum.toString(),
    };
  }, [formatDateComponent]);

  const generateBlogNavigation = useCallback((nav: Set<{ label: string; route: string }>) => {
    nav.add({ label: 'Blog', route: '/blog' });

    const { day, month, year } = params;

    if (!year || !isValidYear(year)) {
      return;
    }

    const dateConfig = buildDateConfig(year, month, day);
    const { BLOG_ROUTE } = WORDPRESS_PATHS;

    // Building hierarchical breadcrumbs based on available date components
    if (dateConfig.year && !dateConfig.month) {
      blogTitle.current = dateConfig.yearLabel;
    }

    if (dateConfig.month && !dateConfig.day) {
      blogTitle.current = dateConfig.monthLabel;
    }

    if (dateConfig.day && !params.post) {
      blogTitle.current = dateConfig.dayLabel;
    }

    if (dateConfig.day && params.post) {
      blogTitle.current = null;
    }

    // Build breadcrumbs hierarchically
    if (dateConfig.month) {
      nav.add({
        label: dateConfig.yearLabel,
        route: `${BLOG_ROUTE}/${dateConfig.year}`,
      });
    }

    if (dateConfig.day) {
      nav.add({
        label: dateConfig.monthLabel,
        route: `${BLOG_ROUTE}/${dateConfig.year}/${dateConfig.month}`,
      });
    }

    if (params.post) {
      nav.add({
        label: dateConfig.dayLabel,
        route: `${BLOG_ROUTE}/${dateConfig.year}/${dateConfig.month}/${dateConfig.day}`,
      });
    }
  }, [params, isValidYear, buildDateConfig]);

  const generatePageNavigation = useCallback((nav: Set<{ label: string; route: string }>) => {
    const pathSegments = match.path.split('/').filter(Boolean);
    pathSegments.pop();
    pathSegments.pop();

    pathSegments.forEach((segment) => {
      nav.add({
        label: upperCaseString(replaceHyphensWithSpace(segment)),
        route: `/${segment}`,
      });
    });
  }, [match.path]);

  const generateGuideAuthorsNavigation = useCallback((nav: Set<{ label: string; route: string }>) => {
    nav.add({
      label: 'Guides',
      route: '/guides',
    });

    if (isGuideAuthorDetailsRoute) {
      nav.add({
        label: 'Authors',
        route: '/guides/authors',
      });
    }
  }, [isGuideAuthorDetailsRoute]);

  const fetchBlogTitleAndTopNav = useCallback(() => {
    const navigation = new Set<{ label: string; route: string }>();

    navigation.add({ label: 'Home', route: '/' });

    if (isBlogRoute) {
      blogTitle.current = LocalT.translate('blogView.mainTitle') as string;
      generateBlogNavigation(navigation);
    } else if (isGuideAuthorsRoute || isGuideAuthorDetailsRoute) {
      blogTitle.current = null;
      generateGuideAuthorsNavigation(navigation);
    } else {
      blogTitle.current = null;
      generatePageNavigation(navigation);
    }

    return {
      blogTitle: blogTitle.current,
      topNav: Array.from(navigation),
    };
  }, [
    generateBlogNavigation,
    generatePageNavigation,
    generateGuideAuthorsNavigation,
    isBlogRoute,
    isGuideAuthorsRoute,
    isGuideAuthorDetailsRoute,
  ]);

  return { fetchBlogTitleAndTopNav };
};
